import type { ReactNode } from 'react';
import {
  Ban, CalendarClock, CheckCircle2, Globe, Mail, MapPin, MessageSquareQuote, NotebookPen,
  Package, Paperclip, Phone, Route, Smartphone, UserPlus, Zap,
} from 'lucide-react';
import { EmptyState, Image, Modal, Skeleton, cn } from '../../ui';
import { RetailerLink, UserLink } from '../../components/EntityLinks';
import { RoleBadge } from '../../components/RoleBadge';
import { productLabel } from '../../lib/format';
import { formatIst } from '../../lib/time';

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Why this is a modal, and why it is coloured
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 *
 * It replaced a right-hand Drawer whose whole body was a bordered `Descriptions` grid: fourteen
 * identically-weighted label/value rows in one grey ladder, so "unrouted, central triage" — the one
 * fact this page exists to clear — sat at the same visual weight as the email address, and the
 * status was a small tag two-thirds of the way down. Reading it meant reading all of it.
 *
 * So the facts are GROUPED BY THE QUESTION THEY ANSWER — what state is this in, what is being asked
 * about, who is asking, who owns it, what has happened to it — and each group gets one colour. The
 * colour is not decoration: it is the status's own hue on the hero and the timeline, and amber
 * wherever something is MISSING that ought not to be. A reader who only glances gets the state, the
 * age and the gap; a reader who needs the email still has it.
 *
 * It is presentational on purpose. Permissions, mutations and confirm dialogs stay on the page, which
 * hands the buttons in as `actions` — so this file never has to know who may claim an inquiry.
 */

/** One tone per lifecycle state, used by the hero wash, the status pill and the timeline dots. */
const TONES = {
  sky: {
    wash: 'bg-sky-500/[0.07] border-sky-500/25 dark:bg-sky-500/[0.10]',
    chip: 'bg-sky-400/15 text-sky-700 dark:bg-sky-400/20 dark:text-sky-300',
    dot: 'bg-sky-500', text: 'text-sky-700 dark:text-sky-300',
  },
  harvest: {
    wash: 'bg-harvest-400/[0.10] border-harvest-400/35 dark:bg-harvest-400/[0.12]',
    chip: 'bg-harvest-100 text-harvest-700 dark:bg-harvest-400/20 dark:text-harvest-300',
    dot: 'bg-harvest-400', text: 'text-harvest-700 dark:text-harvest-300',
  },
  plum: {
    wash: 'bg-plum-400/[0.08] border-plum-400/25 dark:bg-plum-400/[0.11]',
    chip: 'bg-plum-400/15 text-plum-600 dark:bg-plum-400/20 dark:text-plum-300',
    dot: 'bg-plum-400', text: 'text-plum-600 dark:text-plum-300',
  },
  brand: {
    wash: 'bg-brand-500/[0.07] border-brand-500/25 dark:bg-brand-500/[0.10]',
    chip: 'bg-brand-100 text-brand-700 dark:bg-brand-400/20 dark:text-brand-300',
    dot: 'bg-brand-500', text: 'text-brand-700 dark:text-brand-300',
  },
  slate: {
    wash: 'bg-surface-2 border-line',
    chip: 'bg-surface-3 text-muted',
    dot: 'bg-line-strong', text: 'text-muted',
  },
} as const;
type ToneKey = keyof typeof TONES;

/**
 * What each status MEANS, in the words a reader would use.
 *
 * The old drawer printed the raw enum ("NEW") and left the reader to infer the rest. A lead's status
 * is only useful as an instruction — "nobody has picked this up" is actionable, "NEW" is a label.
 */
const STATUS_META: Record<string, { label: string; tone: ToneKey; icon: ReactNode; line: string }> = {
  NEW: { label: 'New', tone: 'sky', icon: <Zap />, line: 'Nobody has picked this up yet.' },
  ASSIGNED: { label: 'Assigned', tone: 'harvest', icon: <UserPlus />, line: 'Someone owns it, but the customer has not been contacted yet.' },
  CONTACTED: { label: 'Contacted', tone: 'plum', icon: <Phone />, line: 'The customer has been spoken to — waiting on an outcome.' },
  // Retired 2026-09-11. Kept so historical rows still render with a sensible name and sentence.
  CONVERTED: { label: 'Converted', tone: 'slate', icon: <CheckCircle2 />, line: 'Closed under a conversion step that no longer exists.' },
  CLOSED: { label: 'Closed', tone: 'slate', icon: <Ban />, line: 'Closed without an order.' },
};
const FALLBACK = { label: 'Unknown', tone: 'slate' as ToneKey, icon: <Zap />, line: '' };

/**
 * How the customer's location was obtained, said in words rather than as a raw enum.
 *
 * It is always the CUSTOMER's own location — never a retailer or farmer master record — and the
 * three sources are not equal evidence: a device fix is measured, a pincode is typed and can be any
 * pincode the visitor felt like entering.
 */
const LOCATION_SOURCE: Record<string, string> = {
  GPS: "the customer's device location",
  PINCODE: "the pincode the customer entered — not a device location",
  CITY: "the city the customer chose — not a device location",
};

/**
 * WHY an enquiry is where it is, in words.
 *
 * Routing used to be a side effect of whichever dealer the visitor clicked, so "why did this go
 * here?" had no answer at all. It is decided from the customer's own location now, and the evidence
 * is graded — a device fix and a hand-typed pincode are different claims, and whoever picks the
 * enquiry up deserves to know which one they are trusting.
 */
const ROUTING_METHOD: Record<string, { label: string; note: string }> = {
  GPS: { label: 'By device location', note: 'measured, the most precise' },
  CITY: { label: 'By the city they gave', note: 'matched to a district' },
  PINCODE: { label: 'By pincode', note: 'typed, so state-level only' },
  STATE: { label: 'By the state they chose', note: 'state-level' },
  DEALER: { label: "By the dealer they picked", note: 'no customer location was given' },
  PROFILE: { label: 'By their registered area', note: 'signed in, sent no location — from their own record' },
  MANUAL: { label: 'Moved by hand', note: 'someone reassigned it' },
  CENTRAL: { label: 'Central queue', note: 'nothing placed it — route it by hand' },
};

/** Who the person said they are. NULL means the form never asked — say that, do not guess. */
const ENQUIRER_TYPE: Record<string, string> = {
  FARMER: 'A farmer', RETAILER: 'A dealer / retailer', DISTRIBUTOR: 'A distributor', OTHER: 'Someone else',
};

const ENQUIRY_TYPE: Record<string, string> = {
  PRODUCT: 'Product enquiry',
  RETAILER: 'Wants to become a dealer',
  DISTRIBUTOR: 'Wants to become a distributor',
  GENERAL: 'General contact',
};

const days = (from?: string | null, to?: string | null) => {
  if (!from) return null;
  const end = to ? new Date(to).getTime() : Date.now();
  return Math.max(0, Math.floor((end - new Date(from).getTime()) / 86_400_000));
};
const dayWord = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
/** Digits only — `wa.me` rejects "+91 98765 43210" but takes 919876543210. */
const waNumber = (v?: string | null) => String(v ?? '').replace(/\D/g, '');

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Small parts
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/** A titled block with a coloured icon — what makes the modal scannable rather than a wall. */
function Section({ icon, title, tint = 'text-brand-600', action, children, className }: {
  icon: ReactNode; title: string; tint?: string; action?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section className={cn('min-w-0', className)}>
      <h3 className="mb-1.5 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 type-label uppercase tracking-[0.06em] text-subtle">
          <span className={cn('shrink-0 [&>svg]:size-3.5', tint)} aria-hidden>{icon}</span>
          <span className="truncate">{title}</span>
        </span>
        {action}
      </h3>
      {children}
    </section>
  );
}

/**
 * One label/value pair.
 *
 * `gap` is the point: a missing dealer or an unnamed territory is not the same fact as a blank, and
 * the app's convention is to name the hole in amber rather than print a grey dash and move on.
 */
/** Statuses where a missing owner is history rather than a gap — nobody is coming for a closed lead. */
const TERMINAL_STATES = ['CONVERTED', 'CLOSED'];

/**
 * A named person plus their ROLE as a coloured pill.
 *
 * The role is the half that stops a name being read as a job title: an inquiry can be claimed by
 * anyone holding INQUIRY.RESPOND, so "Working it now — Bishnu" under a field labelled "Sales person"
 * was telling the reader something false about who Bishnu is. The badge says it in one glance
 * instead, in the same hue that role wears everywhere else in the app.
 */
function Person({ id, name, role }: { id?: string | null; name: string; role?: string | null }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="min-w-0 truncate">{id ? <UserLink id={id}>{name}</UserLink> : name}</span>
      {role ? <RoleBadge role={role} /> : null}
    </span>
  );
}

function Field({ label, value, gap, gapNote }: {
  label: string; value?: ReactNode; gap?: boolean; gapNote?: string;
}) {
  return (
    <div className="min-w-0 rounded-control border border-line bg-surface-2/50 px-3 py-2">
      <div className="type-caption text-subtle">{label}</div>
      {gap ? (
        <div className="mt-0.5 flex items-center gap-1 type-body-strong text-warning-600 dark:text-harvest-300">
          <MapPin className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{gapNote ?? 'not set'}</span>
        </div>
      ) : (
        <div className="mt-0.5 min-w-0 truncate type-body-strong text-content">{value ?? <span className="text-subtle">—</span>}</div>
      )}
    </div>
  );
}

/** One rung of the lifecycle. A step that has not happened stays hollow rather than disappearing. */
function Step({ label, at, note, tone, done, last }: {
  label: string; at?: string | null; note?: string; tone: ToneKey; done: boolean; last?: boolean;
}) {
  const t = TONES[tone];
  return (
    <li className="flex gap-3">
      <span className="flex flex-col items-center" aria-hidden>
        <span className={cn('mt-1 size-2.5 shrink-0 rounded-full ring-2 ring-surface',
          done ? t.dot : 'bg-transparent ring-line-strong')} />
        {!last && <span className={cn('w-px flex-1', done ? 'bg-line-strong' : 'bg-line')} />}
      </span>
      <span className={cn('min-w-0 pb-3', last && 'pb-0')}>
        <span className={cn('block type-body-strong', done ? 'text-content' : 'text-subtle')}>{label}</span>
        <span className="block type-caption text-muted">{at ? formatIst(at) : (note ?? 'not yet')}</span>
      </span>
    </li>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * The modal
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

export function InquiryDetailModal({ open, onClose, inquiry: q, loading, actions }: {
  open: boolean;
  onClose: () => void;
  inquiry: any | null | undefined;
  loading: boolean;
  /** The workflow buttons. Built by the page, which owns the permissions and the mutation. */
  actions?: ReactNode;
}) {
  const meta = (q ? STATUS_META[String(q.status)] : undefined) ?? FALLBACK;
  const tone = TONES[meta.tone];

  /* "AGN" above "AGN 1L" says the product's name twice — the SKU carries the name PLUS the pack, so
     the longer label IS the whole answer and the shorter one is noise. `productLabel` is the same
     rule the list column and every other product cell already use. */
  const product = productLabel(q?.productName, q?.productSku);
  const phone = q?.customerWhatsapp || q?.customerPhone || null;
  const terminalAt = q?.convertedAt ?? q?.closedAt ?? null;
  const age = days(q?.createdAt, terminalAt);
  const ageLine = age == null ? null
    : q.status === 'CONVERTED' ? `closed after ${dayWord(age)}`
      : q.status === 'CLOSED' ? `closed after ${dayWord(age)}`
        : q.status === 'NEW' ? `waiting ${dayWord(age)}`
          : `open ${dayWord(age)}`;

  const hasCoords = q?.latitude != null && q?.longitude != null;
  const place = [q?.city, q?.pincode, q?.stateCode].filter(Boolean).join(' · ');
  /**
   * Does the customer's own state match the dealer's?
   *
   * The two are independent: `territory_id` is copied off the chosen DEALER, while the pincode/state
   * is whatever the visitor gave the public form. A visitor can pick any dealer in the country, so
   * the pair can legitimately disagree — and when it does, the reader deserves to be told rather than
   * left to reconcile "Nashik" against "RAJGANGPUR · 770017" on their own. Only claimed when BOTH
   * halves are known; silence is the right answer to a question we cannot answer.
   */
  const custState = String(q?.customerStateCode ?? q?.stateCode ?? '').trim();
  const dealerState = String(q?.territoryStateCode ?? '').trim();
  const farFromDealer = !!q?.territoryName && !!custState && !!dealerState && custState !== dealerState;
  const attachments: string[] = Array.isArray(q?.attachments) ? q.attachments : [];

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={760}
      title={q ? q.inquiryNo : 'Inquiry'}
      description={q ? `${ENQUIRY_TYPE[q.enquiryType] ?? 'Enquiry'} · ${q.source === 'ANDROID_APP' ? 'from the Android app' : 'from the website'}` : undefined}
      footer={q ? actions : undefined}
    >
      {loading && !q ? (
        <div className="space-y-3">
          <Skeleton className="h-24 rounded-card" />
          <Skeleton className="h-20 rounded-card" />
          <Skeleton className="h-32 rounded-card" />
        </div>
      ) : !q ? (
        <EmptyState title="Could not load this inquiry" />
      ) : (
        <div className="space-y-5">
          {/* ── Hero: the state, what it means, and how long it has been that way ───────────── */}
          {/* Bled to the body's edges (the Modal owns px-5 py-4) so the status colour reads as a
              band rather than a floating card. */}
          <div className={cn('-mx-5 -mt-4 border-b px-5 py-4', tone.wash)}>
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
              <div className="min-w-0">
                <span className={cn('inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 type-label [&>svg]:size-3.5', tone.chip)}>
                  {meta.icon}{meta.label}
                  {ageLine && <span className="font-normal opacity-80">· {ageLine}</span>}
                </span>
                <p className="mt-2 type-body text-content">{meta.line}</p>
                <p className="mt-0.5 truncate type-section-title text-content">{q.customerName}</p>
              </div>

              {/* The pack being asked about — the one picture in here, and the fastest thing to
                  recognise on a lead. */}
              <div className="flex min-w-0 items-center gap-2.5 rounded-card border border-line bg-surface shadow-e1 px-3 py-2">
                {q.productImageUrl ? (
                  <Image src={q.productImageUrl} width={44} height={44} style={{ objectFit: 'cover', borderRadius: 8 }} preview />
                ) : (
                  <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-surface-3 text-subtle">
                    <Package size={18} />
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block type-caption text-subtle">Asking about</span>
                  <span className="block truncate type-body-strong text-content">
                    {product.primary || <span className="text-subtle">no product named</span>}
                  </span>
                  {product.secondary && <span className="block truncate type-caption text-muted">{product.secondary}</span>}
                </span>
              </div>
            </div>
          </div>

          {/* ── Who is asking, and how to reach them ───────────────────────────────────────── */}
          <Section icon={<Phone />} title="How to reach them" tint="text-sky-600 dark:text-sky-400">
            <div className="rounded-card border border-line bg-surface-2/50 p-3">
              {phone || q.customerEmail ? (
                <div className="flex flex-wrap items-center gap-2">
                  {phone && (
                    <>
                      {/* Real links, not printed digits: this modal is opened to make a call. */}
                      <a href={`tel:${phone}`}
                        className="inline-flex items-center gap-1.5 rounded-control border border-line bg-surface px-2.5 py-1.5 type-body-strong text-content transition-colors hover:border-brand-300">
                        <Phone className="size-3.5 text-sky-600 dark:text-sky-400" aria-hidden />{phone}
                      </a>
                      <a href={`https://wa.me/${waNumber(phone)}`} target="_blank" rel="noopener"
                        className="inline-flex items-center gap-1.5 rounded-control border border-brand-500/30 bg-brand-500/10 px-2.5 py-1.5 type-body-strong text-brand-700 transition-colors hover:bg-brand-500/15 dark:text-brand-300">
                        <MessageSquareQuote className="size-3.5" aria-hidden />WhatsApp
                      </a>
                    </>
                  )}
                  {q.customerEmail && (
                    <a href={`mailto:${q.customerEmail}`}
                      className="inline-flex min-w-0 items-center gap-1.5 rounded-control border border-line bg-surface px-2.5 py-1.5 type-body text-content transition-colors hover:border-brand-300">
                      <Mail className="size-3.5 text-plum-500" aria-hidden />
                      <span className="truncate">{q.customerEmail}</span>
                    </a>
                  )}
                </div>
              ) : (
                // A lead with no way to answer it is a dead end, and the modal should say so.
                <p className="type-body text-warning-600 dark:text-harvest-300">
                  No phone or email was left — this lead cannot be followed up.
                </p>
              )}
            </div>
          </Section>

          {/* ── What they actually said ────────────────────────────────────────────────────── */}
          {q.message && (
            <Section icon={<MessageSquareQuote />} title="What they wrote" tint="text-plum-500">
              <blockquote className="rounded-card border border-line border-l-2 border-l-plum-400 bg-surface-2/50 px-3 py-2.5 type-body italic text-content">
                <span className="whitespace-pre-wrap not-italic">{q.message}</span>
              </blockquote>
            </Section>
          )}

          {/* ── Who owns it — the gap this page exists to close ─────────────────────────────── */}
          <Section icon={<Route />} title="Who owns it" tint="text-harvest-600 dark:text-harvest-400">
            {/*
              TWO PEOPLE, TWO LABELS. This was one field called "Sales person" showing
              `assignedToName ?? chosenSalesPersonName` — two different people collapsed by a `??`,
              so whichever one existed took the other's name. It produced the exact reading that was
              reported: "Sales person — Bishnu", where Bishnu is a SUPER ADMIN who claimed the lead,
              while the actual dealer's rep (a Sales Head) was never shown at all. Whoever is WORKING
              a lead can hold any role with INQUIRY.RESPOND; the dealer's rep is a routing fact about
              the dealer. They are different questions and cannot share a label — and each carries the
              person's role as a badge so a non-field role never reads as "the sales person".
            */}
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              <Field
                label="Dealer"
                gap={!q.chosenRetailerName}
                gapNote="no dealer yet · central triage"
                value={q.chosenRetailerId
                  ? <RetailerLink id={q.chosenRetailerId}>{q.chosenRetailerName}</RetailerLink>
                  : q.chosenRetailerName}
              />
              <Field
                label="Dealer's rep"
                value={q.chosenSalesPersonName
                  ? <Person id={q.chosenSalesPersonId} name={q.chosenSalesPersonName} role={q.chosenSalesPersonRole} />
                  : undefined}
                gap={!q.chosenSalesPersonName && !TERMINAL_STATES.includes(q.status)}
                gapNote="dealer has no rep"
              />
              <Field
                label="Working it now"
                value={q.assignedToName
                  ? <Person id={q.assignedToUserId} name={q.assignedToName} role={q.assignedToRole} />
                  : undefined}
                gap={!q.assignedToName && !TERMINAL_STATES.includes(q.status)}
                gapNote="nobody has taken it"
              />
              {/*
                "Dealer's territory", never a bare "Territory". It is derived from the chosen dealer
                (`InquiryService.submit` reads `retailers.territory_id`) and has nothing to do with
                where the customer is — so a lead sent to a Nashik dealer by someone in Rajgangpur
                showed "Territory — Nashik" next to a panel reading "RAJGANGPUR · 770017" and read as
                a straight contradiction. Two places, two owners, two labels.
              */}
              {/* The territory is now the CUSTOMER's, resolved from their own location — not copied
                  off whichever dealer they clicked. The chip says which evidence carried it, because
                  "why is this here?" is the question a hand-over always raises. */}
              <Field
                label="Territory"
                value={q.territoryName
                  ? (
                    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <span className="min-w-0 truncate">{q.territoryName}</span>
                      {q.routingMethod && ROUTING_METHOD[q.routingMethod] && (
                        <span className="shrink-0 rounded-pill bg-surface-3 px-1.5 py-px type-overline text-muted"
                          title={ROUTING_METHOD[q.routingMethod].note}>
                          {ROUTING_METHOD[q.routingMethod].label}
                        </span>
                      )}
                    </span>
                  )
                  : undefined}
                gap={!q.territoryName && !TERMINAL_STATES.includes(q.status)}
                gapNote={q.routingMethod === 'CENTRAL' ? 'central queue — route by hand' : 'not routed yet'}
              />
            </div>
          </Section>

          {/* ── What has happened to it ────────────────────────────────────────────────────── */}
          <div className="grid gap-5 sm:grid-cols-2">
            <Section icon={<CalendarClock />} title="What has happened" tint={tone.text}>
              <ol className="rounded-card border border-line bg-surface-2/50 p-3">
                <Step label="Received" at={q.createdAt} tone={meta.tone} done />
                <Step label="Contacted" at={q.contactedAt} tone={meta.tone} done={!!q.contactedAt} />
                {/* Closing is the only ending an enquiry has. "Converted to an order" was retired on
                    2026-09-11 — the business does not track whether an enquiry leads to a sale. A lead
                    that reached the old CONVERTED state before it went still renders, labelled as the
                    history it is rather than as a rung anyone can still climb. */}
                <Step
                  label={q.status === 'CONVERTED' ? 'Converted (retired step)' : 'Closed'}
                  at={q.status === 'CONVERTED' ? q.convertedAt : q.closedAt}
                  tone="slate" done={TERMINAL_STATES.includes(q.status)} last
                  note={TERMINAL_STATES.includes(q.status) ? undefined : 'not yet'}
                />
              </ol>
            </Section>

            {/* ── Where the lead came from ─────────────────────────────────────────────────── */}
            <Section
              icon={q.source === 'ANDROID_APP' ? <Smartphone /> : <Globe />}
              title="Where it came from"
              tint="text-sky-600 dark:text-sky-400"
            >
              <div className="space-y-2 rounded-card border border-line bg-surface-2/50 p-3">
                <p className="type-body text-content">
                  {q.source === 'ANDROID_APP' ? 'Submitted from the Android app' : 'Submitted from the public website'}
                </p>
                {hasCoords ? (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${q.latitude},${q.longitude}`}
                    target="_blank" rel="noopener"
                    className="inline-flex items-center gap-1.5 rounded-control border border-line bg-surface px-2.5 py-1.5 type-body text-content transition-colors hover:border-brand-300"
                  >
                    <MapPin className="size-3.5 text-danger-500" aria-hidden />
                    {Number(q.latitude).toFixed(4)}, {Number(q.longitude).toFixed(4)}
                    {q.accuracyMeters ? <span className="text-subtle">±{q.accuracyMeters}m</span> : null}
                  </a>
                ) : (
                  <p className="type-body text-muted">{place || <span className="text-subtle">no location given</span>}</p>
                )}
                {/* WHOSE location, and HOW WE GOT IT — both, in words.
                    This said only "location from pincode", which answered neither question, so the
                    line read as a second territory contradicting the dealer's. It is the CUSTOMER's
                    own location and never a master record: `submit` stores exactly what the public
                    form sent, so a typed pincode and a device GPS fix are different evidence and must
                    not be reported in the same words. */}
                {/* Who they said they are. A blank says "not asked" rather than letting the reader assume
                    farmer, which is what the system used to do silently. It is NOT "raised before the
                    form had the question": the product enquiry form has never asked it — only the
                    general Contact form does — so that wording was untrue of every new product lead. */}
                <p className="type-caption text-subtle">
                  {q.enquirerType
                    ? <>They said they are: <span className="text-content">{ENQUIRER_TYPE[q.enquirerType] ?? q.enquirerType}</span></>
                    : 'Not asked who they are — the form they used does not ask.'}
                </p>
                {q.locationSource && q.locationSource !== 'NONE' && (
                  <p className="type-caption text-subtle">
                    {LOCATION_SOURCE[String(q.locationSource)] ?? `the customer's location, from ${String(q.locationSource).toLowerCase()}`}
                    {hasCoords && place ? ` · ${place}` : ''}
                  </p>
                )}
                {/* The enquirer is nowhere near the dealer they picked. Worth saying out loud: it
                    decides whether this lead is really this dealer's to work. */}
                {farFromDealer && (
                  <p className="flex items-start gap-1 type-caption text-warning-600 dark:text-harvest-300">
                    <MapPin className="mt-px size-3 shrink-0" aria-hidden />
                    <span>This is not near {q.territoryName} — the dealer they picked works a different area.</span>
                  </p>
                )}
              </div>
            </Section>
          </div>

          {/* ── Anything staff added ───────────────────────────────────────────────────────── */}
          {(q.staffNotes || q.closedReason) && (
            <Section icon={<NotebookPen />} title="Staff notes" tint="text-harvest-600 dark:text-harvest-400">
              <div className="space-y-2">
                {q.closedReason && (
                  <p className="rounded-control border border-danger-500/25 bg-danger-500/[0.06] px-3 py-2 type-body text-content">
                    <span className="type-label uppercase tracking-[0.06em] text-danger-600 dark:text-danger-400">Closed because</span>
                    <span className="mt-0.5 block whitespace-pre-wrap">{q.closedReason}</span>
                  </p>
                )}
                {q.staffNotes && (
                  <p className="whitespace-pre-wrap rounded-control border border-line bg-surface-2/50 px-3 py-2 type-body text-content">
                    {q.staffNotes}
                  </p>
                )}
              </div>
            </Section>
          )}

          {attachments.length > 0 && (
            <Section icon={<Paperclip />} title={`Attachments · ${attachments.length}`} tint="text-plum-500">
              <div className="flex flex-wrap gap-2">
                {attachments.map((a) => (
                  <Image key={a} src={a} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 8 }} preview />
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </Modal>
  );
}
